import {
  FileCardV2,
  getFileType,
  LinkButtonV2,
  Spacer,
  Text,
  vars,
} from "@vericus/cadmus-ui";

import { BlockAttachmentFragment } from "@/generated/graphql";
import { formatBytes } from "@/utils/file-utils";

import * as styles from "./work-attachment-section.css";

/**
 * Represents an uploaded file attachment
 */
export interface UploadedFile {
  id: string;
  name: string;
  url: string;
  fileSize?: string;
}

/**
 * Display mode for the uploaded attachments list
 * - 'compact': Shows limited attachments with "+N more" button (default)
 * - 'full': Shows all attachments in a scrollable vertical list
 */
export type UploadedAttachmentsDisplayMode = "compact" | "full";

/**
 * Props for the UploadedAttachmentsList component
 */
interface Prop {
  /** Array of uploaded attachments to display */
  attachments: BlockAttachmentFragment[];
  /** Callback fired when a file should be deleted */
  onDelete: (attachmentId: string) => void;
  /** Callback fired when the "more attachments" button is clicked
   * (only used in compact mode)
   * */
  onShowAll?: () => void;
  /** Maximum number of attachments to display before showing "more" button
   * (only used in compact mode)
   * */
  previewLimit?: number;
  /** Display mode for the list */
  displayMode?: UploadedAttachmentsDisplayMode;
}

/**
 * UploadedAttachmentsList component
 *
 * Displays a list of successfully uploaded attachments with options to delete or view them.
 * Supports two display modes:
 * - **Compact**: Shows a limited preview with a "+N more" button
 * - **Full**: Shows all attachments in a vertically scrollable list
 *
 * For Aphrodite (student view):
 * - Shows open-in-browser and delete actions
 * - NO download action (students can only view or remove their uploads)
 *
 * @example
 * ```tsx
 * // Compact mode (default)
 * <UploadedAttachmentsList
 *   attachments={uploadedFiles}
 *   onDelete={(id) => handleDelete(id)}
 *   onShowAll={() => setModalOpen(true)}
 *   previewLimit={2}
 *   displayMode="compact"
 * />
 *
 * // Full mode
 * <UploadedAttachmentsList
 *   attachments={uploadedFiles}
 *   onDelete={(id) => handleDelete(id)}
 *   displayMode="full"
 * />
 * ```
 */
export function UploadedAttachmentsList({
  attachments,
  onDelete,
  onShowAll,
  previewLimit = 2,
  displayMode = "compact",
}: Prop) {
  if (attachments.length === 0) {
    return null;
  }

  const isCompactMode = displayMode === "compact";
  const visibleAttachments = isCompactMode
    ? attachments.slice(0, previewLimit)
    : attachments;
  const remainingCount = attachments.length - previewLimit;
  const hasMoreAttachments = isCompactMode && remainingCount > 0;

  const containerClassName = isCompactMode
    ? styles.attachmentCardsContainer
    : styles.attachmentCardsContainerFull;

  return (
    <>
      <Spacer spacing={24} />
      <div className={styles.filesSection}>
        <div className={styles.filesSectionHeader}>
          <Text kind="label">Uploaded files ({attachments.length})</Text>
          {onShowAll && (
            <LinkButtonV2
              style={{ color: vars.colors.lilac500 }}
              onClick={onShowAll}
            >
              See details
            </LinkButtonV2>
          )}
        </div>
        <Spacer spacing={12} />

        <div className={containerClassName}>
          {visibleAttachments.map((attachment) => {
            return (
              <div key={attachment.id} style={{ maxWidth: 350 }}>
                <FileCardV2
                  fileInfo={{
                    name: attachment.name,
                    type: getFileType(attachment.name),
                    size: attachment.fileSize
                      ? formatBytes(attachment.fileSize)
                      : undefined,
                    extension: attachment.fileExtension ?? undefined,
                  }}
                  onOpenInBrowser={
                    attachment.mediaUrl
                      ? () => window.open(attachment.mediaUrl ?? "", "_blank")
                      : undefined
                  }
                  onDelete={() => onDelete(attachment.id)}
                  // NO onDownload - students can only view or delete
                />
              </div>
            );
          })}
          {hasMoreAttachments && onShowAll && (
            <button
              className={styles.moreAttachmentsButton}
              onClick={onShowAll}
            >
              <Text kind="bodyMd" color="blue500">
                +{remainingCount} more
              </Text>
            </button>
          )}
        </div>
      </div>
    </>
  );
}
